import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { SITE_DEFAULT_OG_IMAGE } from '../data/projects';
import { calculateConfiguratorPrice, AREA_BOUNDS } from '../utils/estimator';
import {
  buildConfiguratorEmailBody,
  configuratorEmailSubject,
} from '../utils/configuratorEmail';
import StepProjectType from '../components/estimate/StepProjectType';
import StepArea from '../components/estimate/StepArea';
import StepLevel from '../components/estimate/StepLevel';
import StepRooms from '../components/estimate/StepRooms';
import StepStyle from '../components/estimate/StepStyle';
import StepExtras from '../components/estimate/StepExtras';
import StepContact from '../components/estimate/StepContact';
import SummaryPanel from '../components/estimate/SummaryPanel';
import { submitLead } from '../utils/submitLead';
import { buildLeadEnvelope } from '../utils/buildLeadPayload';
import { trackEvent } from '../components/Analytics';
import { CONTACT_EMAIL, CONTACT_PHONE_TEL, CONTACT_TELEGRAM_URL, CONTACT_PHONE_DISPLAY } from '../config/siteConstants';

const STORAGE_KEY = 'sati_estimate_state';

const initialState = {
  projectType: 'apartment',
  area: AREA_BOUNDS.default,
  level: 'base',
  bedrooms: 2,
  bathrooms: 1,
  style: 'minimal',
  extras: {
    authorSupervision: false,
    visualizationPack: false,
    furnitureSelection: false,
  },
  contact: {
    name: '',
    email: '',
    phone: '',
    telegram: '',
    notes: '',
  },
};

function stateFromEstimatorNav(navState) {
  if (!navState?.fromEstimator) return null;
  return {
    projectType: typeof navState.projectType === 'string' ? navState.projectType : undefined,
    level: typeof navState.quality === 'string' ? navState.quality : undefined,
    area: typeof navState.area === 'number' ? navState.area : undefined,
  };
}

function isPersistableState(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (typeof obj.projectType !== 'string') return false;
  if (typeof obj.area !== 'number' || !Number.isFinite(obj.area)) return false;
  if (typeof obj.level !== 'string') return false;
  if (typeof obj.bedrooms !== 'number' || !Number.isFinite(obj.bedrooms)) return false;
  if (typeof obj.bathrooms !== 'number' || !Number.isFinite(obj.bathrooms)) return false;
  if (typeof obj.style !== 'string') return false;
  if (!obj.extras || typeof obj.extras !== 'object') return false;
  if (!obj.contact || typeof obj.contact !== 'object') return false;
  return true;
}

function mergeStored(base, parsed) {
  return {
    ...base,
    ...parsed,
    extras: { ...base.extras, ...parsed.extras },
    contact: { ...base.contact, ...parsed.contact },
  };
}

export default function EstimatePage() {
  const { language } = useLanguage();
  const isUA = language === 'UA';
  const location = useLocation();
  const navigate = useNavigate();

  const [estimateState, setEstimateState] = useState(() => {
    let base = { ...initialState };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isPersistableState(parsed)) base = mergeStored(initialState, parsed);
      }
    } catch (_) {
      /* ignore */
    }

    const fromNav = stateFromEstimatorNav(location.state);
    if (fromNav) {
      return {
        ...base,
        ...(fromNav.projectType != null ? { projectType: fromNav.projectType } : {}),
        ...(fromNav.level != null ? { level: fromNav.level } : {}),
        ...(fromNav.area != null ? { area: fromNav.area } : {}),
      };
    }
    return base;
  });

  const [submitHandoff, setSubmitHandoff] = useState(false);
  const [submitSending, setSubmitSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(estimateState));
    } catch (_) {
      /* ignore */
    }
  }, [estimateState]);

  const pricing = useMemo(() => calculateConfiguratorPrice(estimateState), [estimateState]);

  const patch = useCallback((partial) => {
    setEstimateState((prev) => ({ ...prev, ...partial }));
  }, []);

  const toggleExtra = useCallback((key, checked) => {
    setEstimateState((prev) => ({
      ...prev,
      extras: { ...prev.extras, [key]: checked },
    }));
  }, []);

  const patchContact = useCallback((key, value) => {
    setEstimateState((prev) => ({
      ...prev,
      contact: { ...prev.contact, [key]: value },
    }));
  }, []);

  const handleSubmit = async () => {
    const { name, email } = estimateState.contact;
    if (!String(name || '').trim()) {
      setSubmitError(isUA ? 'Вкажіть ім’я в блоці контакту.' : 'Please enter your name in the contact section.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim())) {
      setSubmitError(isUA ? 'Вкажіть коректний email.' : 'Please enter a valid email.');
      return;
    }
    setSubmitError('');
    setSubmitSending(true);
    const textBody = buildConfiguratorEmailBody(estimateState, pricing, isUA);
    const subject = configuratorEmailSubject();
    const c = estimateState.contact;
    const partial = {
      form_name: 'Project builder',
      user_name: String(c.name || '').trim(),
      user_email: String(c.email || '').trim(),
      user_phone: String(c.phone || '').trim(),
      user_telegram: String(c.telegram || '').trim(),
      message: String(c.notes || '').trim(),
      raw_form_payload: textBody,
      selected_project_type: estimateState.projectType,
      selected_area: String(estimateState.area),
      selected_style: estimateState.style,
      selected_rooms: `bedrooms: ${estimateState.bedrooms}, bathrooms: ${estimateState.bathrooms}`,
      selected_extras: JSON.stringify(estimateState.extras),
      selected_category: estimateState.level,
      selected_budget: '',
      selected_timeline: '',
    };
    try {
      const body = buildLeadEnvelope('configurator', partial, {
        locale: language,
        estimateSnapshot: { ...estimateState, pricing },
      });
      body.subject = subject;
      body.textBody = textBody;
      body.contact = estimateState.contact;
      const { ok, status, data } = await submitLead(body);
      if (ok) {
        trackEvent('configurator_submit', { ok: true, partial: Boolean(data?.partial) });
        setSubmitHandoff(true);
        return;
      }
      if (status === 503 && data?.code === 'NO_DESTINATION') {
        setSubmitError(
          isUA
            ? 'Надсилання недоступне: сервер не налаштований. Зателефонуйте або напишіть у Telegram / email.'
            : 'Submission unavailable: server not configured. Call or message Telegram / email.'
        );
        return;
      }
      if (status === 502 && data?.code === 'DELIVERY_FAILED') {
        setSubmitError(
          isUA
            ? 'Не вдалося доставити запит. Спробуйте знову або зв’яжіться напряму.'
            : 'Delivery failed. Retry or contact us directly.'
        );
        return;
      }
      setSubmitError(data?.error || (isUA ? 'Помилка відправки.' : 'Submission failed.'));
    } catch {
      setSubmitError(
        isUA
          ? 'Немає з’єднання. Зв’яжіться телефоном, Telegram або email.'
          : 'No connection. Reach us by phone, Telegram, or email.'
      );
    } finally {
      setSubmitSending(false);
    }
  };

  const title = isUA
    ? 'SATI Architects — конструктор проєкту'
    : 'SATI Architects — project builder';
  const desc = isUA
    ? 'Детальний орієнтовний розрахунок дизайн-проєкту з урахуванням плану та опцій.'
    : 'Detailed indicative design-project estimate with layout and options.';

  return (
    <>
      <SEO title={title} description={desc} ogImage={SITE_DEFAULT_OG_IMAGE} canonicalPath="/estimate" />
      <div className="min-h-screen pt-[calc(var(--sati-header-h)+1.25rem)] md:pt-[calc(var(--sati-header-h)+1.75rem)] pb-20 sm:pb-24 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <nav
            className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-[10px] sm:text-[11px] tracking-[0.14em] uppercase text-muted-foreground border-b border-border/70 pb-6"
            aria-label={isUA ? 'Навігація' : 'Breadcrumb'}
            data-testid="estimate-nav-trail"
          >
            <Link to="/" className="text-foreground/50 hover:text-foreground transition-colors min-h-[44px] inline-flex items-center">
              {isUA ? 'Головна' : 'Home'}
            </Link>
            <span className="text-foreground/20" aria-hidden>
              /
            </span>
            <Link to="/portfolio" className="text-foreground/50 hover:text-foreground transition-colors min-h-[44px] inline-flex items-center">
              {isUA ? 'Проєкти' : 'Projects'}
            </Link>
            <span className="text-foreground/20" aria-hidden>
              /
            </span>
            <span className="text-foreground min-h-[44px] inline-flex items-center">{isUA ? 'Конструктор' : 'Builder'}</span>
            <button
              type="button"
              onClick={() => navigate('/portfolio')}
              className="ml-auto text-foreground/50 hover:text-foreground transition-colors min-h-[44px] px-2 font-medium"
              data-testid="estimate-back-safe"
            >
              {isUA ? 'До портфоліо' : 'Portfolio'}
            </button>
          </nav>
          <header className="mb-12 sm:mb-14 md:mb-16">
            <h1
              className="font-display text-[1.9rem] sm:text-4xl tracking-tight text-foreground"
              style={{ lineHeight: 1.15 }}
            >
              {isUA ? 'Конструктор проєкту' : 'Project builder'}
            </h1>
            <p className="mt-2 font-display italic text-base text-muted-foreground">
              {isUA ? 'детальний орієнтовний прорахунок' : 'detailed indicative estimate'}
            </p>
            <p className="mt-4 max-w-2xl text-[13px] leading-relaxed text-muted-foreground/85">
              {isUA
                ? 'Тут ураховані тип об’єкта, площа, рівень оздоблення, планування, стиль та додаткові послуги. Сума орієнтовна і не замінює комерційну пропозицію після брифу.'
                : 'This view adds typology, area, finish level, layout, style, and optional services. Figures are indicative and do not replace a formal quote after briefing.'}
            </p>
          </header>

          {submitHandoff ? (
            <div
              className="mb-10 border border-border bg-card/30 px-5 py-4 text-sm text-foreground/90 leading-relaxed"
              role="status"
              data-testid="cfg-submit-handoff"
            >
              {isUA ? (
                <>
                  <p className="font-medium text-foreground">Запит надіслано</p>
                  <p className="mt-2 text-muted-foreground">
                    Команда отримає деталі конструктора та зв’яжеться з вами протягом робочого дня. Терміново — телефон або Telegram.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-foreground">Request received</p>
                  <p className="mt-2 text-muted-foreground">
                    Our team will review your builder details and reply within one business day. For urgent matters, call or Telegram.
                  </p>
                </>
              )}
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[11px] tracking-wide uppercase">
                <Link to="/portfolio" className="text-foreground/55 hover:text-foreground underline-offset-4 hover:underline">
                  {isUA ? 'Проєкти' : 'Projects'}
                </Link>
                <Link to="/" className="text-foreground/55 hover:text-foreground underline-offset-4 hover:underline">
                  {isUA ? 'Головна' : 'Home'}
                </Link>
              </div>
            </div>
          ) : null}
          {submitError && !submitHandoff ? (
            <div className="mb-6 rounded border border-red-200 bg-red-50/50 px-4 py-3 text-sm text-red-800 space-y-2" data-testid="cfg-submit-error">
              <p>{submitError}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                <a href={CONTACT_PHONE_TEL} className="underline underline-offset-2">
                  {CONTACT_PHONE_DISPLAY}
                </a>
                <a href={CONTACT_TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  Telegram
                </a>
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-2">
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] gap-12 lg:gap-16 xl:gap-20 items-start lg:isolate">
            <div className="relative z-[1] min-w-0">
              <StepProjectType
                isUA={isUA}
                projectType={estimateState.projectType}
                onChange={(projectType) => patch({ projectType })}
              />
              <StepArea isUA={isUA} area={estimateState.area} onChange={(area) => patch({ area })} />
              <StepLevel isUA={isUA} level={estimateState.level} onChange={(level) => patch({ level })} />
              <StepRooms
                isUA={isUA}
                bedrooms={estimateState.bedrooms}
                bathrooms={estimateState.bathrooms}
                onBedrooms={(bedrooms) => patch({ bedrooms })}
                onBathrooms={(bathrooms) => patch({ bathrooms })}
              />
              <StepStyle isUA={isUA} style={estimateState.style} onChange={(style) => patch({ style })} />
              <StepExtras isUA={isUA} extras={estimateState.extras} onToggle={toggleExtra} />
              <StepContact isUA={isUA} contact={estimateState.contact} onChange={patchContact} />

              <div className="mt-12 lg:hidden">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitSending || submitHandoff}
                  className="w-full py-3 border border-foreground text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-widest rounded-sm min-h-[var(--sati-control-h)] hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-40"
                  data-testid="cfg-submit-mobile"
                >
                  {submitSending ? (isUA ? 'Надсилання…' : 'Sending…') : isUA ? 'Надіслати запит' : 'Send enquiry'}
                </button>
              </div>
            </div>

            <div className="relative z-0 min-w-0 space-y-6 lg:self-start">
              <SummaryPanel isUA={isUA} estimateState={estimateState} />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitSending || submitHandoff}
                className="hidden lg:block w-full py-3 border border-foreground text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-widest rounded-sm min-h-[var(--sati-control-h)] hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-40"
                data-testid="cfg-submit"
              >
                {submitSending ? (isUA ? 'Надсилання…' : 'Sending…') : isUA ? 'Надіслати запит' : 'Send enquiry'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
